import React, { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Wand2, XCircle } from "lucide-react";
import { api } from "@/lib/api";

const fmt = (v) => {
  if (v === null || v === undefined || v === "") return "—";
  if (Array.isArray(v)) return v.map(fmt).join(" × ");
  if (typeof v === "object") return "value" in v ? fmt(v.value) : JSON.stringify(v);
  return String(v);
};
const short = (h) => (h ? `${String(h).slice(0, 10)}…` : "—");

export const P04_STYLE = {
  PARSER_CONFIRMED: "bg-emerald-100 text-emerald-800",
  AI_GUIDED_PARSER_CONFIRMED: "bg-teal-100 text-teal-800",
  HUMAN_CONFIRMED: "bg-blue-100 text-blue-800",
  AI_ONLY: "bg-violet-100 text-violet-800",
  CONFLICT: "bg-red-100 text-red-800",
  REVIEW_REQUIRED: "bg-amber-100 text-amber-800",
  SOURCE_MISSING: "bg-zinc-100 text-zinc-600",
  UNSUPPORTED_AI_CLAIM: "bg-rose-100 text-rose-800",
  READING_READY: "bg-emerald-100 text-emerald-800",
  READING_BLOCKED: "bg-red-100 text-red-800",
  done: "bg-emerald-100 text-emerald-800",
  running: "bg-sky-100 text-sky-800",
  queued: "bg-zinc-100 text-zinc-700",
  failed: "bg-red-100 text-red-800",
  cancelled: "bg-zinc-200 text-zinc-700",
  budget_exceeded: "bg-amber-100 text-amber-800",
};
const St = ({ s }) => <Badge className={`text-[9px] ${P04_STYLE[s] || "bg-zinc-100"}`}>{s || "—"}</Badge>;

/* ── stati campo/feature + gate ───────────────────────────────── */
const StatesTab = ({ ev, eff }) => {
  const fields = (eff?.fields || ev.fields || []);
  const feats = (eff?.features || ev.features || []);
  const gate = eff?.reading_gate || ev.reading_gate || {};
  return (
    <div className="space-y-3" data-testid="p04-states">
      <div className="border border-border p-2">
        <h4 className="text-xs font-semibold flex items-center gap-2">Gate lettura effettivo <St s={gate.status} /></h4>
        {(gate.blockers || []).map((b, i) => (
          <div key={i} className="text-[11px] py-0.5 border-b border-border/50" data-testid={`p04-blocker-${b.code}`}>
            <b>{b.code}</b>: {b.reason} → <span className="text-sky-800">{b.next_action}</span>
          </div>
        ))}
        {(gate.blockers || []).length === 0 && <p className="text-[11px] text-emerald-800">nessun blocco</p>}
        <p className="text-[10px] text-muted-foreground mt-1">{gate.feed_states_note}</p>
      </div>
      <table className="w-full text-[11px]" data-testid="p04-fields-table">
        <thead><tr className="text-left text-muted-foreground">
          <th className="pr-2 py-1">Campo</th><th className="pr-2">Stato</th><th className="pr-2">Parser</th><th className="pr-2">IA</th><th className="pr-2">Valore usabile</th><th className="pr-2">Calcolo?</th><th>Nota</th>
        </tr></thead>
        <tbody>
          {fields.map((f) => (
            <tr key={f.field} className="border-t border-border align-top" data-testid={`p04-field-${f.field}`}>
              <td className="pr-2 py-1 font-medium">{f.field}</td>
              <td className="pr-2"><St s={f.state} /></td>
              <td className="pr-2 break-all">{fmt(f.parser_value)}</td>
              <td className="pr-2 break-all">{fmt(f.ai_claim?.value)}</td>
              <td className="pr-2 break-all font-semibold">{fmt(f.value)}</td>
              <td className="pr-2">{f.quote_eligible ? "sì" : "NO"}</td>
              <td className="max-w-[240px] text-muted-foreground">{f.note || "—"}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <table className="w-full text-[11px]" data-testid="p04-features-table">
        <thead><tr className="text-left text-muted-foreground">
          <th className="pr-2 py-1">Feature</th><th className="pr-2">Stato</th><th className="pr-2">Parser</th><th className="pr-2">Conteggio usabile</th><th className="pr-2">Calcolo?</th><th>Nota</th>
        </tr></thead>
        <tbody>
          {feats.map((f) => (
            <tr key={f.feature_type} className="border-t border-border align-top" data-testid={`p04-feature-${f.feature_type}`}>
              <td className="pr-2 py-1 font-medium">{f.feature_type}</td>
              <td className="pr-2"><St s={f.state} /></td>
              <td className="pr-2">{fmt(f.parser_count)}</td>
              <td className="pr-2 font-semibold">{fmt(f.count)}</td>
              <td className="pr-2">{f.quote_eligible ? "sì" : "NO"}</td>
              <td className="max-w-[280px] text-muted-foreground">{f.note || "—"}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

/* ── voce della coda di revisione ─────────────────────────────── */
const ReviewItem = ({ caseId, item, onResolved }) => {
  const [action, setAction] = useState(null);
  const [value, setValue] = useState("");
  const [motivation, setMotivation] = useState("");
  const [busy, setBusy] = useState(false);
  const bb = item.bbox_page_pt;
  const cropUrl = bb && bb.length === 4
    ? `${api.defaults.baseURL}/calibration/cases/${caseId}/crop-image/${item.page}?x0=${bb[0]}&y0=${bb[1]}&x1=${bb[2]}&y1=${bb[3]}`
    : null;
  const send = async (act) => {
    setBusy(true);
    try {
      await api.post(`/calibration/cases/${caseId}/p04/review-items/${item.item_id}`, {
        action: act, value: act === "modify" ? value : (act === "confirm" ? (item.ai_value ?? item.parser_value) : null), motivation,
      });
      toast.success("Azione registrata (audit + dato etichettato)");
      setAction(null); setValue(""); setMotivation("");
      onResolved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
    setBusy(false);
  };
  const r = item.resolution;
  return (
    <div className="border border-border p-2 space-y-1" data-testid={`p04-review-item-${item.kind}-${item.key}`}>
      <div className="flex items-center gap-2 flex-wrap text-[11px]">
        <St s={item.state} /><b>{item.kind}: {item.key}</b>
        <span className="text-muted-foreground">pagina {item.page}</span>
        {r && <Badge className="text-[9px] bg-blue-100 text-blue-800">risolto: {r.action} da {r.user}</Badge>}
      </div>
      <div className="grid md:grid-cols-2 gap-2">
        <div className="text-[11px] space-y-0.5">
          <div>Parser: <b>{fmt(item.parser_value)}</b> · Proposta IA: <b>{fmt(item.ai_value)}</b>{item.ai_confidence != null ? ` (conf. ${item.ai_confidence})` : ""}</div>
          {item.ai_source_text && <div className="text-muted-foreground">Testo sorgente IA: «{item.ai_source_text}»</div>}
          {(item.zone_texts || []).length > 0 && <div className="text-muted-foreground">Evidenze zona: {(item.zone_texts || []).join(" · ")}</div>}
          <div className="text-amber-800">Motivo: {item.motivation}</div>
          <div className="text-rose-800">Effetto sul preventivo: {item.quote_impact || "—"}</div>
        </div>
        {cropUrl && <img src={cropUrl} alt="crop zona" className="border border-border max-h-40 object-contain justify-self-start" data-testid={`p04-crop-${item.item_id}`} />}
      </div>
      {!r && (
        <div className="space-y-1">
          <div className="flex gap-1 flex-wrap">
            <Button size="sm" className="h-6 text-[11px]" disabled={busy} onClick={() => send("confirm")} data-testid={`p04-confirm-${item.item_id}`}>Conferma</Button>
            <Button size="sm" variant="outline" className="h-6 text-[11px]" disabled={busy} onClick={() => setAction(action === "modify" ? null : "modify")} data-testid={`p04-modify-${item.item_id}`}>Modifica</Button>
            <Button size="sm" variant="outline" className="h-6 text-[11px] text-red-700" disabled={busy} onClick={() => setAction(action === "reject" ? null : "reject")} data-testid={`p04-reject-${item.item_id}`}>Rifiuta</Button>
            <Button size="sm" variant="outline" className="h-6 text-[11px]" disabled={busy} onClick={() => send("not_present")} data-testid={`p04-notpresent-${item.item_id}`}>Dato non presente</Button>
          </div>
          {action === "modify" && (
            <div className="flex gap-1">
              <Input className="h-6 text-[11px] max-w-[160px]" placeholder="valore corretto" value={value} onChange={(e) => setValue(e.target.value)} data-testid={`p04-modify-value-${item.item_id}`} />
              <Textarea className="text-[11px] min-h-[28px]" placeholder="motivazione (obbligatoria)" value={motivation} onChange={(e) => setMotivation(e.target.value)} data-testid={`p04-modify-motivation-${item.item_id}`} />
              <Button size="sm" className="h-6 text-[11px]" disabled={busy || !value || !motivation} onClick={() => send("modify")} data-testid={`p04-modify-send-${item.item_id}`}>Salva</Button>
            </div>
          )}
          {action === "reject" && (
            <div className="flex gap-1">
              <Textarea className="text-[11px] min-h-[28px]" placeholder="motivazione del rifiuto (obbligatoria)" value={motivation} onChange={(e) => setMotivation(e.target.value)} data-testid={`p04-reject-motivation-${item.item_id}`} />
              <Button size="sm" className="h-6 text-[11px]" disabled={busy || !motivation} onClick={() => send("reject")} data-testid={`p04-reject-send-${item.item_id}`}>Rifiuta</Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

/* ── ablation ─────────────────────────────────────────────────── */
const AblationTab = ({ caseId }) => {
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get(`/calibration/cases/${caseId}/p04/ablation`).then((r) => setData(r.data)).catch(() => setData({ missing: true }));
  }, [caseId]);
  if (!data) return <p className="text-xs">Caricamento ablation…</p>;
  if (data.missing) return <p className="text-xs text-amber-800">Ablation non disponibile.</p>;
  return (
    <div className="space-y-2" data-testid="p04-ablation">
      <table className="w-full text-[11px]">
        <thead><tr className="text-left text-muted-foreground">
          <th className="pr-2 py-1">Voce</th><th className="pr-2">Parser</th><th className="pr-2">IA</th><th className="pr-2">Stato P0.4</th><th className="pr-2">Attribuzione</th><th>Nota P0.3-H</th>
        </tr></thead>
        <tbody>
          {(data.rows || []).map((r, i) => (
            <tr key={i} className="border-t border-border align-top" data-testid={`p04-ablation-${r.kind}-${r.key}`}>
              <td className="pr-2 py-1 font-medium">{r.kind}: {r.key}</td>
              <td className="pr-2 break-all">{fmt(r.parser_value)}</td>
              <td className="pr-2 break-all">{fmt(r.ai_value)}</td>
              <td className="pr-2"><St s={r.p04_state} /></td>
              <td className="pr-2 max-w-[220px]"><b>{r.category}</b><div className="text-muted-foreground">{r.category_label}</div></td>
              <td className="max-w-[240px] text-rose-800">{r.p03h_note || "—"}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <div className="text-[10px] text-muted-foreground">{Object.entries(data.counts || {}).map(([k, v]) => `${k}: ${v}`).join(" · ")}</div>
      <p className="text-[10px] text-muted-foreground">{data.nota}</p>
    </div>
  );
};

/* ── chiamate & costi ─────────────────────────────────────────── */
const CostsTab = ({ ev }) => (
  <div className="space-y-2" data-testid="p04-costs">
    <div className="text-[11px]">
      Budget max: <b>${ev.budget?.max_usd}</b> · speso: <b data-testid="p04-cost-spent">${ev.budget?.spent_usd}</b> · superato: <b>{String(ev.budget?.exceeded)}</b>
      {(ev.budget?.skipped || []).length > 0 && <span className="text-amber-800"> · passaggi saltati per budget: {(ev.budget.skipped).join(", ")}</span>}
      {" "}· durata totale: <b>{ev.elapsed_s}s</b>
    </div>
    <table className="w-full text-[10px]">
      <thead><tr className="text-left text-muted-foreground">
        <th className="pr-2 py-1">Passo</th><th className="pr-2">Latenza</th><th className="pr-2">Token in/out</th><th className="pr-2">Costo</th><th className="pr-2">Cache</th><th className="pr-2">Leakage scan</th><th>Payload sha</th>
      </tr></thead>
      <tbody>
        {(ev.calls_light || []).map((c, i) => (
          <tr key={i} className="border-t border-border">
            <td className="pr-2 py-0.5 font-medium">{c.pass}</td>
            <td className="pr-2">{c.latency_s}s</td>
            <td className="pr-2">{c.usage ? `${c.usage.prompt_tokens}/${c.usage.completion_tokens}` : "—"}</td>
            <td className="pr-2">{c.usage?.estimated_cost != null ? `$${Number(c.usage.estimated_cost).toFixed(5)}` : "—"}</td>
            <td className="pr-2">{c.cache_hit ? "riuso" : "no"}</td>
            <td className="pr-2">{c.leakage_scan ? (c.leakage_scan.found?.length ? `⚠ ${c.leakage_scan.found.join(",")}` : `pulito (${c.leakage_scan.n_markers_checked} marcatori)`) : "—"}</td>
            <td className="font-mono">{short(c.payload_sha256)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

/* ── pannello principale ──────────────────────────────────────── */
export const P04Panel = ({ caseId }) => {
  const [data, setData] = useState(null);
  const [job, setJob] = useState(null);
  const [tab, setTab] = useState("revisione");
  const timer = useRef(null);

  const load = useCallback(async () => {
    try {
      const r = await api.get(`/calibration/cases/${caseId}/p04`, { timeout: 120000 });
      setData(r.data);
    } catch (e) { setData({ missing: true, reason: e?.response?.data?.detail }); }
  }, [caseId]);
  useEffect(() => { load(); return () => timer.current && clearInterval(timer.current); }, [load]);

  const poll = useCallback((jobId) => {
    timer.current && clearInterval(timer.current);
    timer.current = setInterval(async () => {
      try {
        const r = await api.get(`/calibration/p04/jobs/${jobId}`);
        setJob(r.data);
        if (!["queued", "running"].includes(r.data.status)) {
          clearInterval(timer.current);
          await load();
        }
      } catch { clearInterval(timer.current); }
    }, 2500);
  }, [load]);

  const run = async () => {
    try {
      const r = await api.post(`/calibration/cases/${caseId}/p04/run`, {});
      setJob(r.data);
      toast.success(r.data.idempotent_reuse ? "Job esistente riusato (niente doppi costi)" : "Job P0.4 avviato in background");
      if (["queued", "running"].includes(r.data.status)) poll(r.data.job_id);
      else await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore avvio P0.4"); }
  };
  const cancel = async () => {
    if (!job) return;
    try { await api.post(`/calibration/p04/jobs/${job.job_id}/cancel`); toast.info("Annullamento richiesto"); } catch { /* noop */ }
  };

  if (!data) return <div className="text-sm p-2" data-testid="p04-loading">Caricamento P0.4…</div>;

  const jobActive = job && ["queued", "running"].includes(job.status);
  const header = (
    <CardHeader className="py-3"><CardTitle className="text-sm flex items-center gap-2 flex-wrap">
      <Wand2 size={15} /> P0.4 — Evidence-guided hybrid (asincrono, non autoritativo)
      {data.meta && <St s={data.meta.gate} />}
      {job && <St s={job.status} />}
    </CardTitle></CardHeader>
  );

  if (data.missing) {
    return (
      <Card data-testid="p04-panel">
        {header}
        <CardContent className="space-y-2">
          <p className="text-xs text-muted-foreground" data-testid="p04-missing">{data.reason || "P0.4 non ancora eseguito."} L'elaborazione gira in background: l'interfaccia resta libera.</p>
          {jobActive ? (
            <div className="flex items-center gap-2 text-xs" data-testid="p04-job-progress">
              <span className="animate-pulse">⏳ {job.progress?.step} ({job.progress?.pct}%)</span>
              <Button size="sm" variant="outline" className="h-6 text-[11px]" onClick={cancel} data-testid="p04-cancel-btn"><XCircle size={12} className="mr-1" />Annulla</Button>
            </div>
          ) : (
            <Button size="sm" onClick={run} data-testid="p04-run-btn"><Wand2 size={13} className="mr-1" /> Esegui P0.4 in background</Button>
          )}
          {job && !jobActive && job.status !== "done" && <p className="text-[11px] text-red-800">Job {job.status}: {job.error || ""} — un nuovo avvio riprende i passaggi già pagati.</p>}
        </CardContent>
      </Card>
    );
  }

  const ev = { ...data.evidence, calls_light: data.evidence.calls || data.evidence.calls_light || [] };
  const m = data.meta || {};
  const unresolved = (data.review_items || []).filter((i) => !i.resolution);
  const TABS = [["revisione", `Coda revisione (${unresolved.length})`], ["stati", "Stati & gate"], ["ablation", "Ablation"], ["costi", "Chiamate & costi"]];
  return (
    <Card data-testid="p04-panel">
      {header}
      <CardContent className="space-y-2">
        <div className="text-[11px] grid md:grid-cols-2 gap-x-6 gap-y-0.5 border border-border p-2 bg-zinc-50" data-testid="p04-meta">
          <div>Modello <b>{m.reader_config?.model}</b> · modalità <b>{m.reader_config?.mode}</b> · chiamate: <b>{m.n_calls}</b> · costo <b>${m.cost_usd}</b> · durata <b>{m.elapsed_s}s</b></div>
          <div>job: <span className="font-mono">{short(m.job_id)}</span> · output sha <span className="font-mono">{short(m.output_sha256)}</span> · commit <span className="font-mono">{short(m.engine_git_commit)}</span></div>
          <div className="md:col-span-2 text-amber-800">{m.dev_regression_note}</div>
        </div>
        {jobActive && (
          <div className="flex items-center gap-2 text-xs" data-testid="p04-job-progress">
            <span className="animate-pulse">⏳ {job.progress?.step} ({job.progress?.pct}%)</span>
            <Button size="sm" variant="outline" className="h-6 text-[11px]" onClick={cancel}>Annulla</Button>
          </div>
        )}
        <div className="flex gap-1 flex-wrap items-center">
          {TABS.map(([k, l]) => (
            <Button key={k} size="sm" variant={tab === k ? "default" : "outline"} className="h-7 text-xs" onClick={() => setTab(k)} data-testid={`p04-tab-${k}`}>{l}</Button>
          ))}
          <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={run} data-testid="p04-rerun-btn">Riavvia (idempotente)</Button>
        </div>
        {tab === "stati" && <StatesTab ev={ev} eff={data.effective} />}
        {tab === "revisione" && (
          <div className="space-y-2" data-testid="p04-review-queue">
            <p className="text-[10px] text-muted-foreground">Una conferma o modifica esplicita diventa HUMAN_CONFIRMED (unico modo umano di alimentare il calcolo). Ogni azione salva utente, timestamp, valori, motivazione, versioni e hash; le baseline restano immutate.</p>
            {(data.review_items || []).map((it) => (
              <ReviewItem key={it.item_id} caseId={caseId} item={it} onResolved={load} />
            ))}
            {(data.review_items || []).length === 0 && <p className="text-xs text-emerald-800">Nessuna voce da revisionare.</p>}
          </div>
        )}
        {tab === "ablation" && <AblationTab caseId={caseId} />}
        {tab === "costi" && <CostsTab ev={ev} />}
      </CardContent>
    </Card>
  );
};
